import React from 'react';
import styles from './style.module.scss';
import GridView from '@mui/icons-material/GridView';
import Map from '@mui/icons-material/Map';
import CheckboxDropdown, { CheckboxDropdownItem } from './checkbox_dropdown';
import SearchInputWithDropdown from 'components/pages/exp-commercial/listings/search/SearchPredictionInput';
import { useCommercialSearchPredictionClick } from 'hooks/use-commercial-search-prediction-click';

import type SearchPrediction from 'data/search-predictions';

export type TopFilters = {
  propertyTypes: Array<string>;
  states: Array<string>;
  cities: Array<string>;
  saleLease: Array<string>;
};

export type TopFilterBarOptions = {
  states: Array<string>;
  cities: Array<string>;
  propertyTypes: Array<string>;
  saleLease: Array<string>;
};

export type ViewType = 'map' | 'grid';

export type TopFilterBarProps = {
  options: TopFilterBarOptions;
  filters: TopFilters;
  onFiltersChanged: (filters: Partial<TopFilters>) => void;
  onToggleView: () => void;
  viewType: ViewType;
  onMapRegionChange?: (region: mapkit.CoordinateRegion | undefined) => void;
  // Search-related props from context
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isLoadingSearchPredictions: boolean;
  searchPredictions: SearchPrediction[];
  isTyping: boolean;
};

export const getFilterLabel = (total: number, selected: number, label: (selected: number) => string, allLabel: string) => {
  if (selected === total) return allLabel;
  return label(selected);
};

export default function TopFilterBar({
  options,
  filters,
  onFiltersChanged,
  onToggleView,
  viewType,
  onMapRegionChange,
  searchQuery,
  setSearchQuery,
  isLoadingSearchPredictions,
  searchPredictions,
  isTyping,
}: TopFilterBarProps) {
  const states = filters.states;
  const cities = filters.cities;
  const propertyTypes = filters.propertyTypes;
  const saleLease = filters.saleLease;

  const handleSearch = (e?: React.FormEvent) => { if (e) e.preventDefault(); };

  // Use the commercial search prediction click hook
  const { handlePredictionClick } = useCommercialSearchPredictionClick({
    onSearch: setSearchQuery,
    onMapRegionChange,
  });


  const handleToggleAllPropertyTypes = (value: boolean) => {
    onFiltersChanged({ propertyTypes: value ? [...options.propertyTypes] : [] });
  };
  const handleTogglePropertyType = (key: string) => onFiltersChanged({ propertyTypes: filters.propertyTypes.includes(key) ? filters.propertyTypes.filter(v => v !== key) : [...filters.propertyTypes, key] });

  const handleToggleState = (value: string) => onFiltersChanged({ states: filters.states.includes(value) ? filters.states.filter(v => v !== value) : [...filters.states, value] });
  const handleToggleCity = (value: string) => onFiltersChanged({ cities: filters.cities.includes(value) ? filters.cities.filter(v => v !== value) : [...filters.cities, value] });
  const handleToggleAllStates = (checked: boolean) => onFiltersChanged({ states: checked ? [...options.states] : [] });
  const handleToggleAllCities = (checked: boolean) => onFiltersChanged({ cities: checked ? [...options.cities] : [] });

  const handleToggleSaleLease = (value: string) => onFiltersChanged({ saleLease: filters.saleLease.includes(value) ? filters.saleLease.filter(v => v !== value) : [...filters.saleLease, value] });
  const handleToggleAllSaleLease = (checked: boolean) => onFiltersChanged({ saleLease: checked ? [...options.saleLease] : [] });

  const propertyTypeItems: CheckboxDropdownItem[] = options.propertyTypes.map(opt => ({ key: opt, label: opt, checked: filters.propertyTypes.includes(opt) }));
  const stateItems: CheckboxDropdownItem[] = options.states.map(opt => ({ key: opt, label: opt, checked: filters.states.includes(opt) }));
  const cityItems: CheckboxDropdownItem[] = options.cities.map(opt => ({ key: opt, label: opt, checked: filters.cities.includes(opt) }));
  const saleLeaseItems: CheckboxDropdownItem[] = options.saleLease.map(opt => ({ key: opt, label: opt, checked: filters.saleLease.includes(opt) }));

  const propertyTypeLabel = getFilterLabel(options.propertyTypes.length, propertyTypes.length, selected => `${selected} Property Types Selected`, 'All Property Types');
  const stateLabel = getFilterLabel(options.states.length, states.length, selected => `${selected} States Selected`, 'All States');
  const cityLabel = getFilterLabel(options.cities.length, cities.length, selected => `${selected} Cities Selected`, 'All Cities');
  const saleLeaseLabel = saleLease.length === options.saleLease.length 
    ? saleLease.join(', ') 
    : saleLease.length === 0 
      ? 'Select Sale/Lease' 
      : saleLease.join(', ');

  return (
    <div className={styles.bar}>
      <SearchInputWithDropdown
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isLoadingSearchPredictions={isLoadingSearchPredictions}
        searchPredictions={searchPredictions}
        isTyping={isTyping}
        handlePredictionClick={handlePredictionClick}
        onSubmit={handleSearch}
      />

      <div className={styles.filters}>
        <div>
          <CheckboxDropdown
            id="sale-lease"
            buttonLabel={saleLeaseLabel}
            items={saleLeaseItems}
            onToggle={key => handleToggleSaleLease(key)}
            onToggleAll={handleToggleAllSaleLease}
            menuAriaLabel="Sale or Lease"
          />
        </div>

        <div>
          <CheckboxDropdown
            id="property-types"
            buttonLabel={propertyTypeLabel}
            items={propertyTypeItems}
            onToggle={key => handleTogglePropertyType(key)}
            onToggleAll={handleToggleAllPropertyTypes}
            menuAriaLabel="Property Types"
          />
        </div>

        <div>
          <CheckboxDropdown
            id="states"
            buttonLabel={stateLabel}
            items={stateItems}
            onToggleAll={handleToggleAllStates}
            onToggle={key => handleToggleState(key)}
            menuAriaLabel="States"
          />
        </div>

        <div>
          <CheckboxDropdown
            id="cities"
            buttonLabel={cityLabel}
            items={cityItems}
            onToggleAll={handleToggleAllCities}
            onToggle={key => handleToggleCity(key)}
            menuAriaLabel="Cities"
          />
        </div>

        <button type="button" className={styles.viewButton} onClick={onToggleView}>
          {viewType === 'map' ? <GridView fontSize="small" /> : <Map fontSize="small" />}
          {viewType === 'map' ? 'List View' : 'Map View'}
        </button>
      </div>
    </div>
  );
}

